import { useMemo } from 'react';
import { geoIdentity, geoPath } from 'd3-geo';
import nsGeo from '../../map/negerisembilan/negerisembilan_dun.json';

// Default fill for a district with no result yet (matches negerisembilan.jsx).
const DEFAULT_FILL = '#e2e6ee';
// Dark seam stroke that stays visible on both the light default fill and the
// saturated alliance colours.
const SEAM = 'rgba(15, 20, 28, 0.85)';

const W = 120;
const H = 96;
const PAD = 6;
// 2.5D extrusion (scaled down from negerisembilan.jsx): the seat is drawn twice — a dark
// "side wall" twin offset down-right behind a soft shadow, then the colour top
// face on top — so the tile reads as a raised 3D slab.
const DEPTH = 4; // extrusion thickness
const WALL_DX = 1; // light from upper-left -> wall drops down-right

// Darken a #rrggbb colour towards black by `amount` (0..1) for the side wall.
// Copied from negerisembilan.jsx; falls back to a neutral grey for any non-hex input.
const shade = (hex, amount = 0.4) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#9aa0ad';
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amount));
  const g = Math.round(((n >> 8) & 255) * (1 - amount));
  const b = Math.round((n & 255) * (1 - amount));
  return `rgb(${r}, ${g}, ${b})`;
};

// Compact single-district SVG. Projects just the one feature whose
// properties.seat === seatCode, using the same planar reflectY projection as the
// full state map (negerisembilan.jsx) so winding/orientation is correct, and
// gives it the matching 2.5D extruded look.
const SeatBoundaryMap = ({ seatCode, fill }) => {
  const d = useMemo(() => {
    const feature = nsGeo.features.find((f) => f.properties.seat === seatCode);
    if (!feature) return null;
    const fc = { type: 'FeatureCollection', features: [feature] };
    // Reserve DEPTH / WALL_DX at the bottom-right so the offset wall isn't clipped.
    const projection = geoIdentity()
      .reflectY(true)
      .fitExtent([[PAD, PAD], [W - PAD - WALL_DX, H - PAD - DEPTH]], fc);
    return geoPath(projection)(feature);
  }, [seatCode]);

  if (!d) return null;

  const shadowId = `seat-shadow-${seatCode}`;
  const topFill = fill || DEFAULT_FILL;

  return (
    <svg
      className="swing-seat-card__map-svg"
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
    >
      <defs>
        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#1c2330" floodOpacity="0.28" />
        </filter>
      </defs>

      {/* Extruded side wall: the seat's dark twin, offset down-right with a soft
          shadow, drawn behind the top face. */}
      <g transform={`translate(${WALL_DX}, ${DEPTH})`} filter={`url(#${shadowId})`}>
        <path d={d} fill={shade(topFill, 0.45)} />
      </g>

      {/* Colour top face on top of the wall. */}
      <path
        d={d}
        fill={topFill}
        stroke={SEAM}
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default SeatBoundaryMap;
